import { ChangeEvent, FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { faExclamationCircle, IconDefinition } from "@fortawesome/pro-regular-svg-icons";

import ReactDOM from "react-dom";

import lemlistIcon from "@/assets/lemlist_logo.png";
import appLogoIcon from "@/assets/images/favicon.png";

import { classNames } from "@/utils/classNames";
import { Icon } from "@/components";

import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

import { CampaignSearchField } from "./CampaignSearchField";

export interface CampaignAutoCompleteItem {
	id: number | string;
	title: string;
	icon?: JSX.Element;
	type?: "internal" | "lemlist";
}

export interface CampaignAutoCompleteProps {
	handleSelect: (value?: CampaignAutoCompleteItem) => void;
	handleSearch?: (value: string) => void;
	handleReset?: () => void;
	selectedItem?: CampaignAutoCompleteItem;
	data: CampaignAutoCompleteItem[];
	label?: string;
	testId?: string;
	floating?: boolean;
	icon?: IconDefinition;
	position?: "top" | "bottom" | "left" | "right" | "bottom-right"; // | "left-center" ;
	className?: string;
	placeholder?: string;
}

export const CampaignAutoComplete: FunctionComponent<CampaignAutoCompleteProps> = ({
	handleSelect,
	handleSearch,
	handleReset,
	selectedItem,
	data,
	label,
	testId,
	position = "bottom",
	className,
	placeholder,
}) => {
	const { t } = useTranslation();
	const [searchValue, setSearchValue] = useState("");

	// Dropdown Logic
	const dropdownRef = useRef<HTMLDivElement>(null);
	const searchFieldRef = useRef<HTMLInputElement>(null);
	const [showDropdown, setShowDropdown] = useState(false);
	const selectFieldRef = useRef<HTMLDivElement>(null);

	const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

	const updateDropdownPosition = useCallback(() => {
		if (selectFieldRef.current) {
			const rect = selectFieldRef.current.getBoundingClientRect();
			const dropdownPositionStyle: React.CSSProperties = {
				position: "fixed",
				width: rect.width,
			};

			// Dynamically set dropdown position based on `position` prop
			switch (position) {
				case "top":
					dropdownPositionStyle.top = rect.top - (dropdownRef.current?.offsetHeight ?? 0);
					dropdownPositionStyle.left = rect.left;
					break;
				case "bottom":
					dropdownPositionStyle.top = rect.bottom;
					dropdownPositionStyle.left = rect.left;
					break;
				case "bottom-right":
					dropdownPositionStyle.top = rect.bottom;
					dropdownPositionStyle.left = rect.left - 120;
					break;
				case "left":
					dropdownPositionStyle.top = rect.top;
					dropdownPositionStyle.left = rect.left - (dropdownRef.current?.offsetWidth ?? 0);
					break;
				case "right":
					dropdownPositionStyle.top = rect.top;
					dropdownPositionStyle.left = rect.right;
					break;
				default:
					dropdownPositionStyle.top = rect.bottom;
					dropdownPositionStyle.left = rect.left;
					break;
			}

			setDropdownStyle(dropdownPositionStyle);
		}
	}, [position]);

	useEffect(() => {
		if (showDropdown) {
			updateDropdownPosition();
			window.addEventListener("resize", updateDropdownPosition);
		} else {
			window.removeEventListener("resize", updateDropdownPosition);
		}

		return () => {
			window.removeEventListener("resize", updateDropdownPosition);
		};
	}, [showDropdown, updateDropdownPosition]);

	const handleSelectItem = (selected: CampaignAutoCompleteItem) => {
		handleSelect(selected);
		setShowDropdown(false);
	};

	const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
		const selectedItem = data.find((item) => item.title === e.target.value);

		handleSearch && handleSearch(e.target.value);

		setSearchValue(e.target.value);

		if (selectedItem) {
			handleSelect(selectedItem);
		}

		setShowDropdown(true);
	};

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			if (!(selectFieldRef.current && selectFieldRef.current?.contains(target as Node))) {
				if (showDropdown) {
					setShowDropdown(false);
				}
			}
		};

		window.addEventListener("click", handleClick);

		return () => window.removeEventListener("click", handleClick);
	}, [selectFieldRef, showDropdown]);

	useEffect(() => {
		setSearchValue(selectedItem?.title || "");
	}, [selectedItem]);

	// Result rendering
	const renderResults = () => {
		const results = data.filter((item) => {
			return item.title
				?.toLowerCase()
				?.replace(" ", "")
				?.includes(searchValue?.toLowerCase()?.replace(" ", ""));
		});

		if (!data.length) {
			return <p className="py-2.5 px-3 text-sm italic text-gray-700">{t("basics.noResults")}</p>;
		}

		return (
			<div
				className={classNames(
					results.length ? "border-b border-b-border" : "",
					"flex flex-col items-start"
				)}
			>
				{results.map((item) => {
					const { id, title, icon, type } = item;

					return (
						<button
							key={`option-select-${id}`}
							className={classNames(
								"flex py-2.5 px-3 pr-8 text-sm text-gray-900 bg-white hover:bg-gray-100 duration-200 cursor-pointer relative items-center w-full text-left"
							)}
							onClick={(e) => {
								e.stopPropagation();
								handleSelectItem({ id, title, type });
							}}
						>
							{icon && <div className="mr-2">{icon}</div>}
							{type === "lemlist" && (
								<div className="mr-2">
									<img alt="Lemlist" className="w-4 h-4" src={lemlistIcon} />
								</div>
							)}
							{type === "internal" && (
								<div className="mr-2">
									<img alt="Lemlist" className="w-4 h-4" src={appLogoIcon} />
								</div>
							)}

							{title}
						</button>
					);
				})}
			</div>
		);
	};

	const handleClickInput = () => {
		setShowDropdown(!showDropdown);
	};

	return (
		<div ref={selectFieldRef} className="relative z-20 w-full">
			{label && (
				<label className="block mb-2 text-sm font-medium text-gray-900 appearance-none">
					{label}
				</label>
			)}
			<div className="relative">
				{handleSearch ? (
					<CampaignSearchField
						ref={searchFieldRef}
						classNameInput={className}
						handleChange={handleSearchChange}
						handleReset={() => {
							setSearchValue("");
							handleSelect();
							handleReset && handleReset();
						}}
						image={
							selectedItem?.type === "internal" ? (
								<img alt="Lemlist" className="w-4 h-4" src={appLogoIcon} />
							) : selectedItem?.type === "lemlist" ? (
								<img alt="Lemlist" className="w-4 h-4" src={lemlistIcon} />
							) : undefined
						}
						name="item"
						placeholder={placeholder || t("basics.pleaseSelect")}
						testId={testId}
						value={searchValue}
						onClick={handleClickInput}
					/>
				) : (
					""
				)}
			</div>
			{showDropdown &&
				ReactDOM.createPortal(
					<div
						ref={dropdownRef}
						className={classNames(
							"mt-1 overflow-hidden bg-white border border-gray-200 rounded-md max-h-[220px] min-w-[300px] overflow-y-auto bb-scrollbar-darker",
							"z-[2000]"
						)}
						style={dropdownStyle}
					>
						{renderResults()}
					</div>,
					document.body
				)}
		</div>
	);
};
